import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import type { ChatCard, CommitDescriptor, DisplayRow, DisplayTone } from '../../types/chat';
import { entityVisual } from './entity-visuals';

// Card for read-tool responses (agenda, tasks, contact, Brain, pipeline, alerts).
// Mostly read-only, but a row may carry a one-tap `action` (e.g. mark a task Done)
// that fires straight through the confirm/commit path via onRowAction.

const DOT_TONE: Record<DisplayTone, string> = {
  good: 'bg-emerald-400',
  warn: 'bg-amber-400',
  bad: 'bg-rose-400',
  accent: 'bg-sky-400',
  muted: 'bg-muted-foreground/40',
};

const SCOPE_LABEL: Record<NonNullable<ChatCard['scope']>, string> = {
  personal: 'Personal',
  team: 'Team',
  company: 'Company',
};

function RowContent({
  row,
  done,
  running,
  onAction,
}: {
  row: DisplayRow;
  done: boolean;
  running: boolean;
  onAction?: () => void;
}) {
  return (
    <>
      {row.tone && (
        <span aria-hidden className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${done ? 'bg-emerald-400' : DOT_TONE[row.tone]}`} />
      )}
      <div className="min-w-0 flex-1">
        <div className={`truncate font-medium ${done ? 'text-muted-foreground line-through' : ''}`}>{row.title}</div>
        {row.subtitle && <div className="truncate text-xs text-muted-foreground">{row.subtitle}</div>}
      </div>
      {row.action && onAction ? (
        <button
          type="button"
          onClick={onAction}
          disabled={done || running}
          className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
            done
              ? 'bg-emerald-500/15 text-emerald-300'
              : 'bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50'
          }`}
        >
          {done ? (
            <>
              <Check className="h-3 w-3" aria-hidden /> {row.action.done ?? row.action.label}
            </>
          ) : running ? (
            '…'
          ) : (
            row.action.label
          )}
        </button>
      ) : (
        row.trailing && (
          <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground tabular-nums">
            {row.trailing}
          </span>
        )
      )}
    </>
  );
}

export function ChatDisplayCard({
  card,
  onRowAction,
}: {
  card: ChatCard;
  onRowAction?: (commit: CommitDescriptor) => Promise<void>;
}) {
  const v = entityVisual(card.entityType);
  // Per-row action state, keyed "groupIndex:rowIndex".
  const [doneKeys, setDoneKeys] = useState<Set<string>>(new Set());
  const [runningKey, setRunningKey] = useState<string | null>(null);

  const handleAction = async (key: string, row: DisplayRow) => {
    if (!onRowAction || !row.action || doneKeys.has(key) || runningKey) return;
    setRunningKey(key);
    try {
      await onRowAction(row.action.commit);
      setDoneKeys((prev) => new Set(prev).add(key));
    } catch {
      toast.error("Couldn't update that. Please try again.");
    } finally {
      setRunningKey(null);
    }
  };

  return (
    <div className="group relative overflow-hidden rounded-xl border border-border bg-card p-3 pl-4 text-sm shadow-card transition-colors motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-1 motion-safe:duration-200">
      <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${v.bar}`} />

      {/* Header */}
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${v.chip}`}>
            <v.Icon className={`h-4 w-4 ${v.icon}`} aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="truncate font-semibold leading-tight">{card.title}</div>
            {card.subtitle && (
              <div className="truncate text-xs text-muted-foreground">{card.subtitle}</div>
            )}
          </div>
        </div>
        {card.scope && (
          <span className="inline-flex shrink-0 items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {SCOPE_LABEL[card.scope]}
          </span>
        )}
      </div>

      {/* Stat tiles */}
      {card.tiles && card.tiles.length > 0 && (
        <div className="mb-2 grid grid-cols-3 gap-1.5">
          {card.tiles.map((t) => (
            <div key={t.label} className="rounded-lg bg-muted/40 px-2 py-1.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t.label}</div>
              <div className="text-base font-semibold tabular-nums leading-tight">{t.value}</div>
              {t.delta && (
                <div
                  className={`text-[10px] ${
                    t.tone === 'up' ? 'text-emerald-400' : t.tone === 'down' ? 'text-rose-400' : 'text-muted-foreground'
                  }`}
                >
                  {t.delta}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Grouped rows */}
      {card.groups?.map((group, gi) => (
        <div key={group.label ?? gi} className={gi > 0 ? 'mt-2' : ''}>
          {group.label && (
            <div className="mb-0.5 mt-1 px-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {group.label}
            </div>
          )}
          <div className="flex flex-col">
            {group.rows.map((row, ri) => {
              const key = `${gi}:${ri}`;
              const done = doneKeys.has(key);
              const running = runningKey === key;
              const content = (
                <RowContent
                  row={row}
                  done={done}
                  running={running}
                  onAction={row.action && onRowAction ? () => void handleAction(key, row) : undefined}
                />
              );
              // A row can deep-link OR carry an action, not both. Action wins.
              return row.link && !row.action ? (
                <a
                  key={key}
                  href={row.link}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-start gap-2 rounded-lg px-1.5 py-1.5 transition-colors hover:bg-muted/50"
                >
                  {content}
                </a>
              ) : (
                <div key={key} className="flex items-start gap-2 rounded-lg px-1.5 py-1.5">
                  {content}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Header-level Open link (e.g. contact record) */}
      {card.link && (
        <div className="mt-2.5">
          <Link
            to={card.link}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
          >
            Open <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}
    </div>
  );
}
